import React from 'react';
import { Box, Typography, Chip, Stepper, Step, StepLabel, StepContent, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt } from 'react-icons/fa';
import {
  AssignmentCard,
  AssignmentCardContent,
  AssignmentDetails,
  ReviewersList,
  ActionButton
} from './styles';
import { EmployeeAvatar } from '../../../../common/EmployeeAvatar';
import ProgressGauge from './ProgressGauge';
import { 
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_BADGES,
  getReviewerStatusText,
  getAssignmentStatusText,
  getAssignmentStatusColor,
  getAssignmentStatusTextColor,
  getReviewerStatus,
  getAssignmentStatusBorderColor,
  getReviewerStatusColor,
  getReviewerStatusBorderColor,
  getReviewerStatusTextColor,
  getAssignmentStatus,
  getStatusDescription,
  getReviewTiming,
  getAssignmentStatusMyPerspective,
  getAssignmentTooltipMyPerspective,
  getBadgeColor,
  getBadgeTextColor,
  getBadgeBorderColor
} from '../../../../../constants/assignmentStatuses';
import { REVIEW_TYPES, getReviewTypeText, getReviewTypeColor, getReviewTypeTextColor } from '../../../../../constants/reviewTypes';
import { getRoleChipStyle } from '../../../../../constants/roleTypes';
import { getTemplateTypeText } from '../../../../../constants/matrixTemplateTypes';
import { getRelativeTime, getDurationFromNow } from '../../../../../utils/dateUtils';
import dayjs from 'dayjs';
import { COLORS, EMPTY_UUID } from '../../../../../constants/radar.js';
import { ReviewerStepLabel, ReviewerEmptyStepLabel, ReviewerStepContent, ReviewerEmptyStepContent } from './ReviewerStep';
import { formatDateOnly } from '../../../../../utils/dateOnly';

const AssignmentCardComponent = ({ assignment, employeeId, onNavigate }) => {
  const navigate = useNavigate();

  const myReviewer = assignment?.reviewers?.find(reviewer => {
    return reviewer?.employee?.id === employeeId;
  });

  if (!myReviewer?.skillsMatrixTemplate?.skillsMatrix) {
    return null;
  }

  const sortedReviewers = [...(assignment?.reviewers || [])].sort((a, b) => a.order - b.order);

  const currentReviewer = assignment.currentReviewerRole;

  const isSelfAssessment = currentReviewer?.order === 1;
  const isCurrentReviewer = currentReviewer?.employee?.id === employeeId;
  const hasEmployeeAlreadyReviewed = myReviewer?.completedAt;
  const isParallelReview = myReviewer?.reviewType === REVIEW_TYPES.PARALLEL;

  // Get progress data from API
  const progressData = assignment.completionProgress || {};

  // Get deadline from reviewProcess if available
  const deadline = assignment.reviewProcess && assignment.reviewProcess.length > 0
    ? assignment.reviewProcess[0].deadlineAt
    : null;

  // Get feedback session information if available
  const feedbackSession = assignment.feedbackSession;

  // ADR-014 §7: a closed (snapshot-closed) session freezes this assignment —
  // no Take/Resume actions; only viewing an already-submitted review remains.
  const sessionClosed = !!(assignment.isLocked || feedbackSession?.completedAt);

  const handleAssignmentAction = () => {
    // Use custom navigation if provided (for scroll state preservation)
    if (onNavigate) {
      onNavigate();
    } else {
      navigate(`/skills-matrix/assignments/${assignment.id}/${employeeId}`);
    }
  };



  return (
    <AssignmentCard>
      <AssignmentCardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 0 }}>
          <EmployeeAvatar employee={assignment.employee} size={60} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: COLORS.PRIMARY }}>
                {assignment.employee.fullName}
              </Typography>
              <Tooltip title={getAssignmentTooltipMyPerspective(getAssignmentStatusMyPerspective(assignment, employeeId))}>
                <Chip
                  label={getAssignmentStatusMyPerspective(assignment, employeeId)}
                  variant="outlined"
                  size="small"
                  sx={{
                    backgroundColor: getBadgeColor(getAssignmentStatusMyPerspective(assignment, employeeId)),
                    color: getBadgeTextColor(getAssignmentStatusMyPerspective(assignment, employeeId)),
                    borderColor: getBadgeBorderColor(getAssignmentStatusMyPerspective(assignment, employeeId)),
                    borderWidth: 1,
                    fontWeight: 500,
                    cursor: 'help',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Box>
            <Typography variant="subtitle2" color="text.primary">
              Assigned to <span style={{ fontWeight: 600, color: COLORS.PRIMARY }}>{myReviewer.skillsMatrixTemplate.skillsMatrix.name}</span> Skills Matrix
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                v.{myReviewer.skillsMatrixTemplate.version}
              </Typography>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748B',
                fontStyle: 'italic',
                display: 'block'
              }}
            >
              {feedbackSession ? (
                <>
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    Part of:
                  </Box>
                  {' '}{feedbackSession.name} {feedbackSession.month}/{feedbackSession.year}
                </>
              ) : (
                'Individual Assignment'
              )}
            </Typography>
          </Box>
        </Box>

        <AssignmentDetails>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}>
            {/* Left side - Assignment Details */}
            <Box sx={{ flex: 1, alignSelf: 'flex-start' }}>
              <div>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Position: </strong>
                  <span>{myReviewer.skillsMatrixTemplate.skillsMatrix.position?.label || 'N/A'}</span>
                </Typography>
              </div>
              
              {myReviewer.skillsMatrixTemplate.skillsMatrix.matrixRoles?.length > 0 && (
                <div>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    <strong>Roles: </strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {myReviewer.skillsMatrixTemplate.skillsMatrix.matrixRoles.map(matrixRole => (
                      <Chip
                        key={matrixRole.id}
                        label={matrixRole.role.label}
                        size="small"
                        sx={{
                          backgroundColor: getRoleChipStyle(matrixRole.role.type).bgColor,
                          color: getRoleChipStyle(matrixRole.role.type).textColor,
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                  </Box>
                </div>
              )}
              
              <div>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>Assigned: </strong>
                  <span>{assignment.assignedAt ? dayjs(assignment.assignedAt).format('MMM D, YYYY') : 'N/A'}</span>
                </Typography>
              </div>

              {deadline && !assignment.completedAt && (
                <div>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaCalendarAlt size={14} color="#475569" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Deadline: </strong>
                        <span>{formatDateOnly(deadline, 'MMM D, YYYY')}</span>
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#64748B',
                          fontStyle: 'italic'
                        }}
                      >
                        {getDurationFromNow(deadline)}
                      </Typography>
                    </Box>
                  </Box>
                </div>
              )}

              {assignment.completedAt && (
                <div>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    <strong>Completed: </strong>
                    <span>{dayjs(assignment.completedAt).format('MMM D, YYYY')}</span>
                  </Typography>
                </div>
              )}
            </Box>

            {/* Right side - Progress Gauge */}
            <ProgressGauge 
              completedSteps={progressData.completedSteps}
              inProgressSteps={progressData.inProgressSteps}
              totalSteps={progressData.totalSteps}
              totalPercentage={progressData.totalPercentage}
              completedPercentage={progressData.completedPercentage}
              completedReviewers={progressData.completedReviewers}
              totalReviewers={progressData.totalReviewers}
              reviewType={myReviewer.reviewType}
            />
          </Box>

          <ReviewersList>
            <Stepper orientation="vertical">
              {sortedReviewers.map((reviewer) => {
                const isCurrentStep = [ASSIGNMENT_STATUSES.IN_PROGRESS, ASSIGNMENT_STATUSES.PENDING_ACTIONS].includes(getReviewerStatus(reviewer));
                const isCompleted = getReviewerStatus(reviewer) === ASSIGNMENT_STATUSES.COMPLETED;
                const timing = getReviewTiming(reviewer);
                const isEmptyReviewer = reviewer.id === EMPTY_UUID;

                return (
                  <Step
                    key={reviewer.id}
                    active={isCurrentStep}
                    completed={isCompleted}
                  >
                    <StepLabel>
                      {!isEmptyReviewer ? (
                        <ReviewerStepLabel
                          reviewer={reviewer}
                          getReviewTypeColor={getReviewTypeColor}
                          getReviewTypeTextColor={getReviewTypeTextColor}
                          getReviewTypeText={getReviewTypeText}
                          getReviewerStatusText={getReviewerStatusText}
                          getTemplateTypeText={getTemplateTypeText}
                        />
                      ) : (
                        <ReviewerEmptyStepLabel />
                      )}
                    </StepLabel>
                    <StepContent>
                      {!isEmptyReviewer ? (
                        <ReviewerStepContent
                          reviewer={reviewer}
                          timing={timing}
                        />
                      ) : (
                        <ReviewerEmptyStepContent />
                      )}
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
          </ReviewersList>
          
          {(isCurrentReviewer || hasEmployeeAlreadyReviewed || isParallelReview)
            && !(sessionClosed && !hasEmployeeAlreadyReviewed) && (
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
              position: 'sticky',
              bottom: 0,
              pt: 1,
              backgroundColor: '#f8f9fa',
              borderTop: '1px solid #e0e0e0'
            }}>
              <ActionButton
                onClick={handleAssignmentAction}
              >
                {hasEmployeeAlreadyReviewed
                  ? 'View Assignment'
                  : (isParallelReview
                      ? (!myReviewer.startedAt
                          ? 'Take Assignment'
                          : !myReviewer.completedAt
                            ? 'Resume Assignment'
                            : 'View Assignment')
                      : (isSelfAssessment 
                          ? (!currentReviewer.startedAt 
                              ? 'Take Assignment'
                              : !currentReviewer.completedAt 
                                ? 'Resume Assignment'
                                : 'View Assignment')
                          : (!currentReviewer.startedAt
                              ? 'Review Assignment'
                              : !currentReviewer.completedAt
                                ? 'Resume Assignment'
                                : 'View Assignment')))}
              </ActionButton>
            </Box>
          )}
        </AssignmentDetails>
      </AssignmentCardContent>
    </AssignmentCard>
  );
};

export default AssignmentCardComponent; 